'use client';

import { formatPrice } from '@/lib/format-price';

interface LineItemPriceProps {
  /** Effective price the customer pays for this line. */
  price: number;
  /** Undiscounted line price; when greater than `price` the line is marked discounted. */
  originalPrice?: number;
  currency: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE_CLASS: Record<NonNullable<LineItemPriceProps['size']>, string> = {
  sm: 'text-[13.5px]',
  md: 'text-[18px]',
  lg: 'text-[20px]',
  xl: 'text-[24px]',
};

/**
 * Per-row cart price. When `originalPrice` is greater than `price`, renders the
 * original struck through beside it.
 *
 * Drawn here rather than through the design system's <Price> for the same
 * reason lib/format-price exists: <Price> prints öre this catalogue never uses
 * and carries its own colour, so the cart and the listing card would show the
 * same product two different ways.
 *
 * Shared by the mini-cart, full cart page, and checkout summary.
 */
export function LineItemPrice({ price, originalPrice, currency, size = 'md' }: LineItemPriceProps) {
  const isDiscounted = originalPrice !== undefined && originalPrice > price;

  return (
    <span className="inline-flex min-w-0 flex-wrap items-baseline justify-end gap-x-2 gap-y-1 text-right">
      <span className={`font-heading font-bold text-foreground ${SIZE_CLASS[size]}`}>
        {formatPrice(price, currency)}
      </span>
      {isDiscounted && (
        <span className="line-through text-[12.5px] font-normal text-foreground/45">
          {formatPrice(originalPrice, currency)}
        </span>
      )}
    </span>
  );
}
